import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { BehaviorSubject, catchError, combineLatest, concat, filter, map, Observable, of, shareReplay, Subject, switchMap, timer } from 'rxjs';

import { IElectron } from 'electron-app/electron.interface';
import type { CryptlexLicense, LicenseStatus } from 'electron-app/licenses';
import { ILicensingService } from 'shared-modules-angular/licensing';

import { LicensedFeature } from '../../../core/config/feature-flags.config';

export interface LicenseStatusIndicator {
  message: string;
  status: 'warning' | 'danger' | null;
  showStatus: boolean;
  days?: number;
}

const LICENSED_FEATURE_IDS: LicensedFeature[] = ['inspection', 'analysis'];

function isLicenseActive(license: CryptlexLicense, isInGracePeriod: boolean): boolean {
  return license.status === 'activated' || isInGracePeriod;
}

// Emits the feature's current validity immediately, then flips to `false` exactly at its
// expiry (if any) and stays there. `feature.enabled`/`feature.expires` already come from
// LicenseValidator - this only schedules the flip at the right time.
function featureValidityOverTime(feature: CryptlexLicense['features'][LicensedFeature]): Observable<boolean> {
  if (!feature.enabled) {
    return of(false);
  }

  if (feature.expires === false) {
    return of(true);
  }

  const delayUntilExpiry: number = feature.expires * 1000 - Date.now();

  return delayUntilExpiry > 0 ? concat(of(true), timer(delayUntilExpiry).pipe(map(() => false))) : of(false);
}

@Injectable({
  providedIn: 'root',
})
export class CryptlexLicenseService implements ILicensingService<LicensedFeature> {
  public readonly defaultLicenseStatusIndicator: LicenseStatusIndicator = { message: '', status: null, showStatus: false };

  /**
   * The `LicensedFeatureId`s currently valid (license active AND, per feature, not yet
   * expired) - see `ILicensingService`. Empty while no license has been pushed yet, or
   * while the license is inactive.
   */
  public readonly validLicenses$: Observable<LicensedFeature[]>;

  private readonly _electron: IElectron = window.electron;
  private readonly _gracePeriodDaysLimit: number = 30;
  private readonly _daysThresholdBeforeExpirationToShowWarning: number = 5;

  private readonly _handleLicenseActivationSubject: Subject<LicenseStatus> = new Subject<LicenseStatus>();
  private readonly _handleLicenseDeactivationSubject: Subject<LicenseStatus> = new Subject<LicenseStatus>();
  private readonly _handleLicenseBehaviorSubject: BehaviorSubject<CryptlexLicense | undefined> = new BehaviorSubject<CryptlexLicense | undefined>(undefined);
  private readonly _handleLicenseStatusIndicatorBehaviorSubject: BehaviorSubject<LicenseStatusIndicator> =
    new BehaviorSubject<LicenseStatusIndicator>(this.defaultLicenseStatusIndicator);

  public handleLicenseActivation$: Observable<LicenseStatus>;
  public handleLicenseDeactivation$: Observable<LicenseStatus>;
  public handleLicense$: Observable<CryptlexLicense>;
  public handleLicenseStatusIndicator$: Observable<LicenseStatusIndicator>;

  private _isInGracePeriod: boolean;

  public get isInGracePeriod(): boolean {
    return this._isInGracePeriod;
  }

  constructor() {
    this.handleLicenseActivation$ = this._handleLicenseActivationSubject.asObservable();
    this.handleLicenseActivation();
    this.handleLicense$ = this._handleLicenseBehaviorSubject.asObservable();
    this.handleLicense();
    this.validLicenses$ = this.handleLicense$.pipe(
      filter((license): license is CryptlexLicense => !!license),
      switchMap(license => (isLicenseActive(license, this.isInGracePeriod)
        ? combineLatest(
          LICENSED_FEATURE_IDS.map(featureId => featureValidityOverTime(license.features[featureId]).pipe(map(valid => (valid ? featureId : null)))),
        ).pipe(map(ids => ids.filter((id): id is LicensedFeature => id !== null)))
        : of([]))),
      catchError(() => of([])),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    this.handleLicenseDeactivation$ = this._handleLicenseDeactivationSubject.asObservable();
    this.handleLicenseDeactivation();
    this.handleLicenseStatusIndicator$ = this._handleLicenseStatusIndicatorBehaviorSubject.asObservable();
  }

  public activateLicense(perpetualKey: string): void {
    this._electron.send('activateLicense', perpetualKey);
  }

  public deactivateLicense(): void {
    this._electron.send('deactivateLicense');
  }

  public getLicense(): void {
    this._electron.send('getLicense');
  }

  private handleLicenseActivation(): void {
    this._electron?.subscribe('handleLicenseActivation', (status: LicenseStatus) => {
      this._handleLicenseActivationSubject.next(status);
    });
  }

  private handleLicense(): void {
    this._electron?.subscribe('handleLicense', (license: CryptlexLicense) => {
      this.validateGracePeriod(license);

      this.updateLicenseStatusIndicator(license);

      this._handleLicenseBehaviorSubject.next(license);
    });
  }

  private handleLicenseDeactivation(): void {
    this._electron?.subscribe('handleLicenseDeactivation', (status: LicenseStatus) => {
      this._handleLicenseDeactivationSubject.next(status);
    });
  }

  private validateGracePeriod(license: CryptlexLicense): void {
    if (license.status !== 'expired') {
      return;
    }

    const gracePeriodDate: DateTime = DateTime.fromSeconds(license.expiryDate).plus({ days: this._gracePeriodDaysLimit });
    const currentDate: DateTime = DateTime.now();

    this._isInGracePeriod = gracePeriodDate > currentDate;
  }

  private needToShowExpirationWarning(numberOfDaysBeforeExpiration: number): boolean {
    return numberOfDaysBeforeExpiration >= 0 && numberOfDaysBeforeExpiration <= this._daysThresholdBeforeExpirationToShowWarning;
  }

  private updateLicenseStatusIndicator(license: CryptlexLicense): void {
    if (license.expiryDate === 0) { // perpetual license
      this._handleLicenseStatusIndicatorBehaviorSubject.next(this.defaultLicenseStatusIndicator);

      return;
    }

    const expirationDate: DateTime = DateTime.fromSeconds(license.expiryDate);
    const currentDate: DateTime = DateTime.now();
    const numberOfDaysBeforeExpiration: number = Math.round(expirationDate.diff(currentDate, 'days').days);

    if (this.needToShowExpirationWarning(numberOfDaysBeforeExpiration)) {
      this._handleLicenseStatusIndicatorBehaviorSubject.next({
        message: `License - ${numberOfDaysBeforeExpiration} days left`,
        status: 'warning',
        showStatus: true,
        days: numberOfDaysBeforeExpiration,
      });

      return;
    }

    const numberOfDaysAfterExpiration: number = Math.round(currentDate.diff(expirationDate, 'days').days);

    if (this.isInGracePeriod) {
      this._handleLicenseStatusIndicatorBehaviorSubject.next({
        message: `License expired - ${numberOfDaysAfterExpiration} days`,
        status: 'danger',
        showStatus: true,
        days: numberOfDaysAfterExpiration,
      });

      return;
    }

    if (numberOfDaysAfterExpiration > this._gracePeriodDaysLimit) {
      this._handleLicenseStatusIndicatorBehaviorSubject.next({
        message: 'Imminent Lock',
        status: 'danger',
        showStatus: true,
      });
    }
  }
}
